/* Original, dependency-free progressive enhancement. Checkout owns its existing lifecycle. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 801px)');
  const revealEnabled = document.body.dataset.enableReveal === 'true';
  const parallaxEnabled = document.body.dataset.editorialParallax === 'true';
  const seen = new WeakSet(), activeMedia = new Set();
  let revealObserver, mediaObserver, frame = 0;
  const reveal = element => { element.classList.add('vx-in'); revealObserver?.unobserve(element); };
  function initReveals(scope = document) {
    if (!revealEnabled || reduced.matches || !('IntersectionObserver' in window)) return;
    revealObserver ||= new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { threshold: 0.04, rootMargin: '0px 0px -12px 0px' });
    scope.querySelectorAll('[data-motion-reveal],.section-heading,.product-grid>.product-card').forEach(element => {
      if (seen.has(element)) return;
      seen.add(element);
      // Above-the-fold content stays visible; an intro never gates interaction.
      if (element.getBoundingClientRect().top < innerHeight || element.contains(document.activeElement)) return;
      const siblings = element.parentElement?.children;
      if (element.matches('.product-card') && siblings) element.style.setProperty('--vx-reveal-delay', `${Math.min(120, ([...siblings].indexOf(element) % 4) * 35)}ms`);
      element.classList.add('vx-motion-ready');
      revealObserver.observe(element);
    });
  }
  function drawParallax() {
    frame = 0;
    if (reduced.matches || !desktop.matches) return;
    const samples = [...activeMedia].filter(el => el.isConnected).map(el => {
      const r = el.getBoundingClientRect();
      return [el, Math.max(-18, Math.min(18, (innerHeight / 2 - r.top - r.height / 2) * 0.035))];
    });
    // All reads precede all writes; only viewport-adjacent campaign media is sampled.
    samples.forEach(([el, y]) => el.style.setProperty('--vx-parallax-y', `${y.toFixed(2)}px`));
  }
  function requestParallax() { if (!frame && activeMedia.size && !reduced.matches && desktop.matches) frame = requestAnimationFrame(drawParallax); }
  function initParallax(scope = document) {
    if (!parallaxEnabled || reduced.matches || !desktop.matches || !('IntersectionObserver' in window)) return;
    mediaObserver ||= new IntersectionObserver(entries => {
      entries.forEach(entry => entry.isIntersecting ? activeMedia.add(entry.target) : activeMedia.delete(entry.target));
      requestParallax();
    }, { rootMargin: '120px 0px' });
    scope.querySelectorAll('[data-editorial-parallax]').forEach(el => mediaObserver.observe(el));
  }
  function setMotionPreference() {
    if (reduced.matches || !desktop.matches) {
      if (frame) cancelAnimationFrame(frame);
      frame = 0; activeMedia.clear(); mediaObserver?.disconnect();
      document.querySelectorAll('[data-editorial-parallax]').forEach(el => el.style.removeProperty('--vx-parallax-y'));
    }
    if (reduced.matches) {
      revealObserver?.disconnect();
      document.querySelectorAll('.vx-motion-ready').forEach(reveal);
    } else { initReveals(); initParallax(); }
  }
  function initMerchandising(scope = document) {
    scope.querySelectorAll('[data-merch-grid]').forEach(section => {
      if (section.dataset.merchReady) return;
      section.dataset.merchReady = 'true';
      const cards = [...section.querySelectorAll('[data-merch-products]>.product-card')];
      const controls = section.querySelector('[data-merch-controls]');
      if (!controls || !cards.length) return;
      let categories = 0;
      controls.querySelectorAll('[data-merch-filter]').forEach(button => {
        if (button.dataset.merchFilter === 'all') return;
        button.hidden = !cards.some(card => card.dataset.merchCategory === button.dataset.merchFilter);
        if (!button.hidden) categories++;
      });
      if (!categories) return;
      controls.hidden = false;
      controls.addEventListener('click', event => {
        const button = event.target.closest('[data-merch-filter]');
        if (!button || button.hidden) return;
        const category = button.dataset.merchFilter;
        controls.querySelectorAll('button').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
        let count = 0;
        cards.forEach(card => {
          card.hidden = category !== 'all' && card.dataset.merchCategory !== category;
          if (!card.hidden) { count++; reveal(card); }
        });
        const status = section.querySelector('[data-merch-status]');
        if (status) status.textContent = `${count} ${count === 1 ? 'piece' : 'pieces'} in this selection.`;
      });
    });
  }
  function initBrands(scope = document) {
    scope.querySelectorAll('.vx-brand-directory-full').forEach(section => {
      if (section.dataset.brandsReady) return;
      section.dataset.brandsReady = 'true';
      const brands = [...section.querySelectorAll('.vx-brand[data-brand-initial]')];
      const index = section.querySelector('[data-brand-index]'), count = section.querySelector('[data-brand-count]');
      if (count) count.textContent = `${brands.length} ${brands.length === 1 ? 'label' : 'labels'} in the directory`;
      if (!index) return;
      const initials = new Set();
      brands.forEach((brand, i) => {
        const initial = brand.dataset.brandInitial;
        if (initials.has(initial)) return;
        initials.add(initial);
        brand.id = `${section.id}-initial-${i}`;
        const link = document.createElement('a'); link.href = `#${brand.id}`; link.textContent = initial;
        link.setAttribute('aria-label', `Brands beginning with ${initial}`); index.appendChild(link);
      });
    });
  }
  function improveOptionDisplay(scope = document) {
    scope.querySelectorAll('[data-option-display]').forEach(span => {
      const source = span.previousElementSibling?.value;
      const compact = /^(\d+(?:\.\d+)?)USMEN\/(\d+(?:\.\d+)?)USWOMEN\/(\d+(?:\.\d+)?)EU$/.exec(source || '');
      if (compact) span.textContent = `${compact[1]} US Men / ${compact[2]} US Women / ${compact[3]} EU`;
      // Input values, variant option strings, IDs and selected-state matching are never modified.
    });
  }
  let purchaseObserver;
  function initPurchasePanel() {
    const panels = [...document.querySelectorAll('.product-detail')].filter(el => !el.closest('#quick-view'));
    if (!panels.length || !('ResizeObserver' in window)) return;
    purchaseObserver?.disconnect();
    const update = () => {
      const height = document.querySelector('#site-header')?.getBoundingClientRect().height || 80;
      document.documentElement.style.setProperty('--vx-header-height', `${height}px`);
      panels.forEach(panel => panel.classList.toggle('vx-purchase-fits', desktop.matches && panel.getBoundingClientRect().height < innerHeight - height - 45));
    };
    purchaseObserver = new ResizeObserver(update); panels.forEach(panel => purchaseObserver.observe(panel));
    const header = document.querySelector('#site-header'); if (header) purchaseObserver.observe(header);
    update();
  }
  function init(scope = document) { initReveals(scope); initParallax(scope); initMerchandising(scope); initBrands(scope); improveOptionDisplay(scope); initPurchasePanel(); }
  init();
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });
  reduced.addEventListener('change', setMotionPreference);
  desktop.addEventListener('change', () => { setMotionPreference(); initPurchasePanel(); });
  document.addEventListener('focusin', event => {
    const target = event.target.closest('.vx-motion-ready'); if (target) reveal(target);
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-editorial-parallax]').forEach(el => { mediaObserver?.unobserve(el); activeMedia.delete(el); });
    event.target.querySelectorAll('.vx-motion-ready').forEach(el => revealObserver?.unobserve(el));
  });
})();
